'use client';

import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';
import { useThemeMode } from '../theme/useThemeMode';
import { resolveIconName } from './utils';
import type { TechnologyIconName } from './supported-icon-names';
import type {
  TechnologyIconSources,
  TechnologyIconVariant,
} from './technology-icon-sources';
import {
  loadTechnologyIcon,
  readCachedTechnologyIcon,
} from './technology-icon-cache';

export type TechnologyIconProps = {
  name: string;
  showInitials?: boolean;
  size?: number;
  variant?: TechnologyIconVariant;
  className?: string;
  style?: CSSProperties;
};

type LoadedTechnologyIcon = {
  name: TechnologyIconName;
  sources: TechnologyIconSources;
};

const initialsFor = (name: string): string =>
  name
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .slice(0, 2)
    .join('');

const useTechnologyIconSources = (
  iconName: TechnologyIconName | null,
): TechnologyIconSources | undefined => {
  const [loaded, setLoaded] = useState<LoadedTechnologyIcon | null>(null);

  useEffect(() => {
    if (!iconName || readCachedTechnologyIcon(iconName)) {
      return;
    }

    let cancelled = false;

    loadTechnologyIcon(iconName)
      .then((sources) => {
        if (!cancelled) {
          setLoaded({ name: iconName, sources });
        }
      })
      .catch((error: unknown) => {
        console.warn('Failed to load technology icon; showing placeholder', {
          error: error instanceof Error ? error : new Error(String(error)),
          iconName,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [iconName]);

  if (!iconName) {
    return undefined;
  }

  return (
    readCachedTechnologyIcon(iconName) ??
    (loaded?.name === iconName ? loaded.sources : undefined)
  );
};

export function TechnologyIcon({
  name,
  showInitials,
  size,
  variant,
  className,
  style,
}: TechnologyIconProps) {
  const { mode } = useThemeMode();
  const iconName = resolveIconName(name);
  const iconVariant = variant ?? (mode === 'dark' ? 'dark' : 'light');
  const sources = useTechnologyIconSources(iconName);

  if (iconName) {
    const sizeStyle =
      size !== undefined ? { width: size, height: size, ...style } : style;
    const boxStyle: CSSProperties = { display: 'inline-block', ...sizeStyle };

    if (!sources) {
      return <span className={className} style={boxStyle} />;
    }

    return (
      <img
        alt=""
        src={sources[iconVariant]}
        className={className}
        style={boxStyle}
        draggable={false}
      />
    );
  }

  if (!showInitials) {
    return null;
  }

  return initialsFor(name) || null;
}
